/**
 * Mapper LinkResolvedBindings → ConversionEventData por tipo de evento.
 *
 * Chaves semânticas conhecidas (productId, etc.) alimentam o payload oficial.
 * Chaves desconhecidas não vazam no evento.
 */

import type { ConversionEventData } from '@/events/conversion';

import type { ConversionEventType } from '../../contracts';
import type {
  LinkResolvedBindings,
  LinkResolvedValue,
} from './LinkValueResolver';

export type LinkMapResult =
  | {
      readonly ok: true;
      readonly data: ConversionEventData;
      readonly unknownKeys?: readonly string[];
    }
  | {
      readonly ok: false;
      readonly reason: string;
      readonly unknownKeys?: readonly string[];
    };

const ADD_TO_CART_KEYS = new Set([
  'id',
  'priceId',
  'productId',
  'offerId',
  'priceCode',
  'productName',
  'price',
  'currency',
  'quantity',
]);

const REMOVE_KEYS = new Set([
  'id',
  'priceId',
  'productId',
  'priceCode',
  'quantity',
]);

const VIEW_CONTENT_KEYS = new Set([
  'id',
  'priceId',
  'productId',
  'offerId',
  'contentId',
  'priceCode',
  'value',
  'price',
  'currency',
]);

const LEAD_KEYS = new Set(['email', 'phone', 'name']);

const CHECKOUT_KEYS = new Set(['value', 'currency']);

function asString(value: LinkResolvedValue | undefined): string | undefined {
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function asNumber(value: LinkResolvedValue | undefined): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return undefined;
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : undefined;
  }
  return undefined;
}

function collectUnknown(
  values: LinkResolvedBindings,
  known: ReadonlySet<string>
): string[] {
  return Object.keys(values).filter(key => !known.has(key));
}

/**
 * Eventos que a source `link` pode produzir com segurança nesta etapa.
 */
export function isLinkSupportedEvent(eventType: ConversionEventType): boolean {
  switch (eventType) {
    case 'ADD_TO_CART':
    case 'REMOVE_FROM_CART':
    case 'VIEW_CONTENT':
    case 'SEARCH':
    case 'LEAD':
    case 'INITIATE_CHECKOUT':
      return true;
    case 'ADD_PAYMENT_INFO':
    case 'PURCHASE':
      return false;
    default:
      return false;
  }
}

export function mapLinkBindingsToEvent(
  eventType: ConversionEventType,
  values: LinkResolvedBindings
): LinkMapResult {
  switch (eventType) {
    case 'ADD_TO_CART': {
      const unknownKeys = collectUnknown(values, ADD_TO_CART_KEYS);
      const explicitPriceId =
        asString(values.priceId) ?? asString(values.priceCode);
      const productId = asString(values.productId) ?? explicitPriceId;
      const offerId = asString(values.offerId);
      const id = asString(values.id) ?? explicitPriceId;
      const productName = asString(values.productName);
      const price = asNumber(values.price);
      const currency = asString(values.currency);
      const quantity = asNumber(values.quantity);
      if (!productId && !explicitPriceId) {
        return {
          ok: false,
          reason: 'ADD_TO_CART link source requires priceId or productId',
          unknownKeys,
        };
      }
      const data = {
        ...(id ? { id } : {}),
        ...(explicitPriceId ? { priceId: explicitPriceId } : {}),
        ...(productId ? { productId } : {}),
        ...(offerId ? { offerId } : {}),
        ...(productName ? { productName } : {}),
        ...(price != null ? { price } : {}),
        ...(currency ? { currency } : {}),
        ...(quantity != null ? { quantity } : {}),
      };
      return { ok: true, data, ...(unknownKeys.length ? { unknownKeys } : {}) };
    }

    case 'REMOVE_FROM_CART': {
      const unknownKeys = collectUnknown(values, REMOVE_KEYS);
      const explicitPriceId =
        asString(values.priceId) ?? asString(values.priceCode);
      const productId = asString(values.productId) ?? explicitPriceId;
      const id = asString(values.id) ?? explicitPriceId;
      const quantity = asNumber(values.quantity);
      if (!productId && !explicitPriceId) {
        return {
          ok: false,
          reason: 'REMOVE_FROM_CART link source requires priceId or productId',
          unknownKeys,
        };
      }
      return {
        ok: true,
        data: {
          ...(id ? { id } : {}),
          ...(explicitPriceId ? { priceId: explicitPriceId } : {}),
          ...(productId ? { productId } : {}),
          ...(quantity != null ? { quantity } : {}),
        },
        ...(unknownKeys.length ? { unknownKeys } : {}),
      };
    }

    case 'VIEW_CONTENT': {
      const unknownKeys = collectUnknown(values, VIEW_CONTENT_KEYS);
      const contentId = asString(values.contentId);
      const productId = asString(values.productId);
      const explicitPriceId =
        asString(values.priceId) ?? asString(values.priceCode);
      const offerId = asString(values.offerId);

      if (
        contentId &&
        productId &&
        contentId !== productId &&
        !explicitPriceId
      ) {
        return {
          ok: false,
          reason: 'VIEW_CONTENT contentId/productId conflict',
          unknownKeys,
        };
      }

      const resolvedId =
        explicitPriceId ?? contentId ?? productId ?? asString(values.id);
      if (!resolvedId) {
        return {
          ok: false,
          reason: 'VIEW_CONTENT requires priceId, contentId, or productId',
          unknownKeys,
        };
      }

      const value = asNumber(values.value) ?? asNumber(values.price);
      const currency = asString(values.currency);
      return {
        ok: true,
        data: {
          ...(explicitPriceId
            ? {
                id: asString(values.id) ?? explicitPriceId,
                priceId: explicitPriceId,
              }
            : {}),
          contentId: contentId ?? resolvedId,
          productId: productId ?? resolvedId,
          ...(offerId ? { offerId } : {}),
          ...(value != null ? { value } : {}),
          ...(currency ? { currency } : {}),
        },
        ...(unknownKeys.length ? { unknownKeys } : {}),
      };
    }

    case 'SEARCH': {
      const query = asString(values.query);
      if (!query) {
        return { ok: false, reason: 'SEARCH requires query' };
      }
      return {
        ok: true,
        data: { query },
        unknownKeys: collectUnknown(values, new Set(['query'])),
      };
    }

    case 'LEAD': {
      const unknownKeys = collectUnknown(values, LEAD_KEYS);
      const email = asString(values.email);
      const phone = asString(values.phone);
      const name = asString(values.name);
      if (!email && !phone && !name) {
        return {
          ok: false,
          reason: 'LEAD requires email, phone, or name',
          unknownKeys,
        };
      }
      return {
        ok: true,
        data: {
          ...(email ? { email } : {}),
          ...(phone ? { phone } : {}),
          ...(name ? { name } : {}),
        },
        ...(unknownKeys.length ? { unknownKeys } : {}),
      };
    }

    case 'INITIATE_CHECKOUT': {
      const unknownKeys = collectUnknown(values, CHECKOUT_KEYS);
      const value = asNumber(values.value);
      const currency = asString(values.currency);
      return {
        ok: true,
        data: {
          ...(value != null ? { value } : {}),
          ...(currency ? { currency } : {}),
        },
        ...(unknownKeys.length ? { unknownKeys } : {}),
      };
    }

    case 'ADD_PAYMENT_INFO':
      return {
        ok: false,
        reason: 'link source does not support ADD_PAYMENT_INFO',
      };

    case 'PURCHASE':
      return {
        ok: false,
        reason: 'link source does not support PURCHASE in this stage',
      };

    default:
      return { ok: false, reason: `unsupported event: ${eventType}` };
  }
}
